/**
 * Follow-ups in the running session (pedagogy spec §5.3.6, §5.3.8) as pure functions: the contrast
 * twin a terminal answer offers, the state it is inserted with, and the fields its first try sends
 * with the answer. `SessionPage` calls them; they are unit-tested without the DOM.
 *
 * A contrast twin arrives with the offer (its public question rides on `followUps`), so inserting it
 * needs no request: the student taps a prediction, then "Gör kontrastuppgiften", and the twin
 * lands right after the question it follows, carrying the prediction. The first try of any inserted
 * question sends `followUp { of, kind }` (and `prediction` for a contrast); a retry sends neither,
 * because the server inherits both from the first try.
 */
import {
  insertAfter,
  type FollowUpState,
  type PracticeSessionData,
  type QuestionResult,
} from "./session";
import type { AnswerBody, ContrastOffer, PredictionVerb } from "./types";

/** The three direction verbs of a verb-form prediction, in the order they are shown. */
export const PREDICTION_VERBS: readonly PredictionVerb[] = ["increases", "decreases", "unchanged"];

/** The follow-up fields a first try sends: none for an ordinary question. */
export function followUpBody(
  result: Pick<QuestionResult, "followUp"> | undefined,
): Pick<AnswerBody, "followUp" | "prediction"> {
  const followUp = result?.followUp;
  if (!followUp) return {};
  return {
    followUp: { of: followUp.of, kind: followUp.kind },
    ...(followUp.prediction ? { prediction: followUp.prediction.choice } : {}),
  };
}

/** What an inserted "similar problem" carries: only the question it follows. */
export function similarState(of: string): FollowUpState {
  return { of, kind: "similar" };
}

/**
 * What an inserted contrast twin carries: what it changes and, when the link authors a prediction,
 * its prompt, options and the choice made before insertion.
 */
export function contrastState(
  of: string,
  offer: ContrastOffer,
  choice: string | undefined,
): FollowUpState {
  const prediction = offer.prediction;
  return {
    of,
    kind: "contrast",
    varies: offer.varies,
    ...(prediction && choice !== undefined
      ? {
          prediction: {
            prompt: prediction.prompt,
            ...(prediction.options ? { options: prediction.options } : {}),
            choice,
          },
        }
      : {}),
  };
}

/**
 * The session with the offered twin inserted right after `sourceId`, carrying its follow-up state,
 * or `null` when the source is gone or the twin is already in the session.
 */
export function insertContrast(
  data: PracticeSessionData,
  sourceId: string,
  offer: ContrastOffer,
  choice: string | undefined,
): PracticeSessionData | null {
  return insertAfter(data, sourceId, offer.question, contrastState(sourceId, offer, choice));
}

/** A contrast offer as the "Nästa steg" card shows it. */
export interface ContrastOfferView {
  offer: ContrastOffer;
  /** `added`: the twin was inserted from this answer, with the prediction made for it. */
  status: "open" | "added";
  /** The prediction behind an `added` offer, a verb or an option id. */
  choice?: string;
}

/**
 * The offers of `sourceId`'s terminal answer that the card still shows. An offer whose twin is
 * already in the session is `added` when this answer inserted it, and left out when something else
 * did (Today's plan, the other source): `insertAfter` would refuse it anyway.
 */
export function contrastOfferViews(
  data: PracticeSessionData,
  sourceId: string,
  offers: readonly ContrastOffer[] | undefined,
): ContrastOfferView[] {
  const views: ContrastOfferView[] = [];
  for (const offer of offers ?? []) {
    const inSession = data.results[offer.questionId];
    if (!inSession) {
      views.push({ offer, status: "open" });
    } else if (inSession.followUp?.kind === "contrast" && inSession.followUp.of === sourceId) {
      const choice = inSession.followUp.prediction?.choice;
      views.push({ offer, status: "added", ...(choice !== undefined ? { choice } : {}) });
    }
  }
  return views;
}
